/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect } from 'react'
import { useHookstate } from '@hookstate/core'
import Layout from '~/components/Layout'
import { useLang } from '~/hooks/useLang'
import CrudIndex from '~/components/CrudIndex'
import ConfirmDialog from '~/components/ConfirmDialog'
import TransferDialog from '../components/TransferDialog'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { deactivateEquipment } from '../services'
import notify from '~/utils/notify'
import { ActionButtonsProps } from '~/components/CrudIndex/interfaces/ICrudIndex'
import userUserState from '~/features/User/stores/UserState'

// no types required since results are not rendered here
export default function Index() {
  const { translate } = useLang()
  const { verifyPermission } = userUserState()
  const equipment = useHookstate<string | null>(null)
  const equipmentGroupId = useHookstate<number | null>(null)
  const agencyId = useHookstate<number | null>(null)
  const warehouseId = useHookstate<number | null>(null)
  const isAvailable = useHookstate<boolean | null>(null)
  const active = useHookstate<boolean | null>(null)
  const forceRefresh = useHookstate(false)
  const loading = useHookstate(false)
  const openTransfer = useHookstate(false)
  const transferEquipmentId = useHookstate<number | null>(null)
  const transferEquipmentName = useHookstate<string | null>(null)
  const transferIsAvailable = useHookstate<boolean>(true)
  const openDialogConfirmDeactivate = useHookstate(false)
  const equipmentActionId = useHookstate<number | null>(null)
  const equipmentActionName = useHookstate<string | null>(null)

  useEffect(() => {
    document.title = translate('Resources - Dispatch System')
  }, [])

  const handleDeactivateEquipment = async (id: number | null) => {
    if (!id) return
    loading.set(true)
    try {
      await deactivateEquipment(id)
      notify({ message: translate('Equipment deactivated with success!'), type: 'success' })
      openDialogConfirmDeactivate.set(false)
      forceRefresh.set(!forceRefresh.get())
    } catch (err) {
      console.error(err)
    } finally {
      loading.set(false)
    }
  }

  const extraActions = React.useMemo(() => {
    const actions: ActionButtonsProps[] = []

    actions.push({
      iconButton: (params) => params['active'] === 'Sim' ? <ToggleOnIcon /> : <ToggleOffOutlinedIcon />,
      label: (params) => params['active'] === 'Sim' ? translate('Deactivate equipment') : translate('Equipment deactivated'),
      tooltipText: (params) => params['active'] === 'Sim' ? translate('Deactivate equipment') : translate('Equipment deactivated'),
      action: (params) => {
        equipmentActionId.set(params.id)
        equipmentActionName.set(params['name'])
        openDialogConfirmDeactivate.set(true)
      },
      showInMenu: function (params) {
        const hasAccess = params['hasAccess'] === 0;
        const isActive = params['active'] === 'Sim';

        return hasAccess || !isActive ? true : false
      },
      disableTooltip: false,
    })

    actions.push({
      iconButton: <SwapHorizIcon />,
      label: translate('Transfer Equipment'),
      tooltipText: (params) => params['active'] === 'Não' ? translate('You cannot perform this action because the equipment has been disabled') : translate('Transfer Equipment'),
      action: (params) => {
        transferEquipmentId.set(params.id)
        transferEquipmentName.set(params['name'])
        transferIsAvailable.set(params['is_available'])
        openTransfer.set(true)
      },
      showInMenu: function (params) {
        const hasAccess = params['hasAccess'] === 0;
        const isActive = params['active'] === 'Sim';

        return hasAccess || !isActive ? true : false
      },
      disableTooltip: false,
    })

    return actions
  }, [])

  return (
    <Layout marginLayout={true}>
      <CrudIndex
        model='equipment'
        title='Equipments'
        createTitleComplement='Equipament'
        aliasLabel={[
          { column: 'equipment_group:name', label: translate('Equipment Group') },
          { column: 'agency:name', label: translate('Agency:name') },
          { column: 'warehouse:name', label: translate('Warehouse') },
          { column: 'serial_number', label: translate('Serial Number') },
          { column: 'active', label: translate('Active') },
        ]}
        booleanField={['active']}
        notSortable={['equipment_group:name', 'agency:name', 'warehouse:name', 'serial_number']}
        forceOrderColumns={['name', 'code', 'serial_number', 'agency:name', 'warehouse:name', 'equipment_group:name', 'active']}
        filters={[
          { name: 'equipment', type: 'name', variable: equipment },
          { name: 'equipment_group_id', type: 'equipment_group_id', variable: equipmentGroupId },
          { name: 'agency_id', type: 'agency_id', variable: agencyId },
          { name: 'warehouse_id', type: 'warehouse_id', variable: warehouseId },
          { name: 'active', type: 'boolean', placeholder: 'Ativo', variable: active },
        ]}
        clearFilters={() => {
          equipment.set(null)
          equipmentGroupId.set(null)
          agencyId.set(null)
          warehouseId.set(null)
          isAvailable.set(null)
          active.set(null)
          forceRefresh.set(!forceRefresh.get())
        }}
        searchButton={() => forceRefresh.set(!forceRefresh.get())}
        forceRefresh={forceRefresh.get()}
        disableColumnsFilters
        showCreateButton={true}
        canView={true}
        editable={true}
        desableDelete={false}
        extraActions={extraActions}
        rowActionDisabled={(row) => !row.hasAccess}
        showInViewMore={[
          'name',
          'code',
          'serial_number',
          'equipment_group:name',
          'agency:name',
          'warehouse:name',
          'active',
          'created_at',
        ]}
        viewTitle={'Equipment Details'}
      />

      <TransferDialog
        open={openTransfer}
        equipmentId={transferEquipmentId.get()}
        equipmentName={transferEquipmentName.get()}
        isAvailable={transferIsAvailable.get()}
        handleClose={() => openTransfer.set(false)}
        onTransferSuccess={() => forceRefresh.set(!forceRefresh.get())}
      />

      <ConfirmDialog
        open={openDialogConfirmDeactivate}
        title={translate('Deactivate equipment')}
        content={translate(
          'Confirm desactivate the equipment? By performing this action, the equipment will no longer be usable.',
          { equipmentName: equipmentActionName.get() }
        )}
        handleClose={() => openDialogConfirmDeactivate.set(false)}
        handleAgree={() => handleDeactivateEquipment(equipmentActionId.get())}
        loading={loading}
        AgreeLabel={translate('inutilize')}
      />
    </Layout>
  )
}
